import dayjs from 'dayjs';
import { merge } from 'lodash';
import { computed, InjectionKey, reactive, ref } from 'vue';
import { SCHEDULE_TYPES, SCHEDULE_TYPE_CODES } from '@/constants/Schedule';
import { Program } from '@/modules/Program';
import { MonthlySchedule } from '@/modules/Schedule';

interface Data {
  prev: MonthlySchedule | null;
  current: MonthlySchedule | null;
  next: MonthlySchedule | null;
}

export const useSchedule = (today = dayjs().subtract(4, 'hour')) => {
  /**
   * 日別番組表の開始時間
   */
  const DAILY_SCHEDULE_HOUR_START = 4;
  /**
   * 日別番組表の終了時間
   */
  const DAILY_SCHEDULE_HOUR_END = 28;
  /**
   * 1日は24時間
   */
  const HOURS_PER_DAY = 24;
  /**
   * 隙間放送枠用のダミー番組ID
   */
  const BLANK_PROGRAM_ID = '__blank__';

  const date = ref();

  const data = reactive<Data>({
    prev: null,
    current: null,
    next: null,
  });

  const allSchedules = computed(() => {
    const schedules: MonthlySchedule = {};
    if (data.prev) merge(schedules, data.prev);
    if (data.current) merge(schedules, data.current);
    if (data.next) merge(schedules, data.next);
    return schedules;
  });

  const month = computed(() => ({
    prev: today.subtract(1, 'month').format('YYYYMM'),
    current: today.format('YYYYMM'),
    next: today.add(1, 'month').format('YYYYMM'),
  }));

  const selectableDate = computed(() => {
    const result: { label: string; value: string }[] = [];
    if (data.prev) result.push(..._genDateList(month.value.prev));
    if (data.current) result.push(..._genDateList(month.value.current));
    if (data.next) result.push(..._genDateList(month.value.next));
    return result;
  });

  const _genDateList = (month: string) => {
    const days = dayjs(month).daysInMonth();
    const result: { label: string; value: string }[] = [];
    for (let i = 1; i <= days; i++) {
      const day = dayjs(month).set('date', i);
      result.push({ label: day.format('M/D(ddd)'), value: day.format('YYYYMMDD') });
    }
    return result;
  };

  /**
   * 指定日、指定チャンネルの番組表情報を返す
   * @param date YYYYMMDD
   * @param channel
   * @returns {Program[]}
   */
  const getSchedule = (date: string, channel: 'tch1' | 'tch2'): Program[] => {
    try {
      return allSchedules.value[date][channel] || [];
    } catch {
      return [];
    }
  };

  const formattedPrograms = ref<(Program & { block: number })[]>([]);
  const blockPerHour = ref<number[]>(Array(HOURS_PER_DAY).fill(0));

  const _genBlankProgram = (start: Program['start'], end: Program['end']): Program =>
    new Program(BLANK_PROGRAM_ID, '', null, null, 'tch1', null, null, start, end, null, null, null, [], null, null);

  const formatProgram = (schedules: Program[]) => {
    for (let i = 0; i < schedules.length; i++) {
      // 最初の番組がAM4:00から始まらない場合、ダミー番組を挿入する
      if (i === 0) {
        const _start = dayjs(date.value).set('hour', DAILY_SCHEDULE_HOUR_START);
        if (dayjs(schedules[i].start).unix() > _start.unix()) {
          const _dummy = _genBlankProgram(_start.format('YYYYMMDDHHmmss'), schedules[i].start);
          formattedPrograms.value.push({
            block: _calcProgramBlock(_dummy),
            ..._dummy,
          });
        }
      }

      // i番目の番組と(i-1)番目の番組の間に隙間時間がある場合、ダミー番組を挿入する
      else if (schedules[i].start !== schedules[i - 1].end) {
        const _dummy = _genBlankProgram(schedules[i - 1].end, schedules[i].start);
        formattedPrograms.value.push({
          block: _calcProgramBlock(_dummy),
          ..._dummy,
        });
      }

      formattedPrograms.value.push({ block: _calcProgramBlock(schedules[i]), ...schedules[i] });

      // i番目の番組が28:00（翌4:00）より早く終了する場合、ダミー番組を挿入する
      if (i === schedules.length - 1) {
        const _end = dayjs(date.value).set('hour', DAILY_SCHEDULE_HOUR_END);
        if (dayjs(schedules[i].end).unix() < _end.unix()) {
          const _dummy = _genBlankProgram(schedules[i].end, _end.format('YYYYMMDDHHmmss'));
          formattedPrograms.value.push({
            block: _calcProgramBlock(_dummy),
            ..._dummy,
          });
        }
      }
    }
  };

  const init = (selectedDate: string) => {
    date.value = selectedDate;
    formattedPrograms.value.splice(0);
    blockPerHour.value.splice(0);
    blockPerHour.value.push(...Array(HOURS_PER_DAY).fill(0));
  };

  /**
   * 番組のブロック数を求める
   *
   * ブロック数：当該番組の属する時間帯の数
   * 通常番組は最小単位が2, ダミー番組は1となる
   * @example
   * - 12:00 - 12:30 の番組の場合、ブロック数 2
   * - 12:30 - 13:30 の番組の場合、ブロック数 4
   * - 12:00 - 13:30 の番組の場合、ブロック数 4
   * - 12:00 - 12:30 のダミー番組の場合、ブロック数 1
   * - 12:30 - 13:30 のダミー番組の場合、ブロック数 2
   * - 12:00 - 13:30 のダミー番組の場合、ブロック数 3
   */
  const _calcProgramBlock = (program: Program): number => {
    const _s = dayjs(program.start);
    const _e = dayjs(program.end);
    const startHour = Math.max(
      dayjs(date.value).date() === _s.date() ? _s.hour() : _s.hour() + HOURS_PER_DAY,
      DAILY_SCHEDULE_HOUR_START
    );
    const endHour = Math.min(
      dayjs(date.value).date() === _e.date() ? _e.hour() : _e.hour() + HOURS_PER_DAY,
      DAILY_SCHEDULE_HOUR_END
    );

    // 2ブロック単位で扱う
    let blockUnit = 2;

    const start = parseInt(dayjs(program.end).format('HH')) * 60 + parseInt(dayjs(program.end).format('mm'));
    const end = parseInt(dayjs(program.start).format('HH')) * 60 + parseInt(dayjs(program.start).format('mm'));
    if (start - end == 5 && !isBlankProgram(program)) {
      blockUnit -= 1;
    }
    const block =
      (endHour - startHour) * blockUnit +
        (_e.minute() === 0 || endHour === DAILY_SCHEDULE_HOUR_END /* NOTE: 深夜4時以降にブロックを伸長させない */
          ? 0
          : blockUnit) || blockUnit; /* 最低でも1単位分のブロックは確保する */

    _calcHourBlock(startHour, block, blockUnit);

    const correctedBlock = ref<number>(block);

    // ダミー番組を考慮し、番組ごとのブロック数・時間ごとのブロック数を調整
    if (isBlankProgram(program)) {
      const correctBlocks = (hour: number) => {
        correctedBlock.value -= 1;
        blockPerHour.value[hour - DAILY_SCHEDULE_HOUR_START] -= 1;
      };

      if (startHour === endHour && _s.minute() !== 0 && _e.minute() !== 0) {
        // 例： 10:30-10:40
        correctBlocks(startHour);
      } else if (_s.minute() !== 0 && _e.minute() !== 0) {
        // 例： 10:30-12:30
        correctBlocks(startHour);
        correctBlocks(endHour);
      } else if (_s.minute() !== 0 && _e.minute() === 0) {
        // 例： 10:30-12:00
        correctBlocks(startHour);
      } else if (_s.minute() === 0 && _e.minute() !== 0) {
        // 例： 10:00-12:30
        correctBlocks(endHour);
      }
    }

    return correctedBlock.value;
  };

  /**
   * 時間ごとのブロック数を求める
   *
   * この時点ではダミー番組を考慮しない
   */
  function _calcHourBlock(hour: number, block: number, blockUnit: number) {
    for (let i = 0; i < block / blockUnit; i++) {
      blockPerHour.value[hour - DAILY_SCHEDULE_HOUR_START + i] += blockUnit;
    }
  }

  /**
   * 分表示をしないかどうか
   *
   * NOTE: 前日からの日跨ぎ番組の場合は、分表示しない
   */
  const hideMinute = (start: Program['start']): boolean =>
    dayjs(start).unix() < dayjs(date.value).set('hour', DAILY_SCHEDULE_HOUR_START).unix();

  const startMinute = (start: Program['start']) => {
    if (!start) return null;
    return hideMinute(start) ? null : start.substring(10, 12);
  };

  /**
   * 番組のエピソード番号を返す
   * @param episode {string} 例:0010
   * @returns {string} 例:#10, null
   */
  const episodeNumber = (episode: Program['episode']) => {
    return Number(episode) ? ` #${Number(episode)}` : '';
  };

  const ampm = (hour: number) => (hour < 12 ? '午前' : hour < 24 ? '午後' : '深夜');

  const isActiveHour = (hour: number, now = dayjs()) => {
    if (hour <= HOURS_PER_DAY) {
      return dayjs(date.value).format('YYYYMMDD') === now.format('YYYYMMDD') && now.hour() === hour;
    }
    // hour > 24 の場合
    return (
      dayjs(date.value).add(1, 'day').format('YYYYMMDD') === now.format('YYYYMMDD') &&
      now.hour() === hour - HOURS_PER_DAY
    );
  };
  const isActiveProgram = (program: Program, now = dayjs()) => {
    return dayjs(program.start).unix() <= now.unix() && now.unix() < dayjs(program.end).unix();
  };
  const isBlankProgram = (program: Program) => program.id === BLANK_PROGRAM_ID;

  const isFiveMinute = (program: Program) => {
    const start = parseInt(dayjs(program.end).format('HH')) * 60 + parseInt(dayjs(program.end).format('mm'));
    const end = parseInt(dayjs(program.start).format('HH')) * 60 + parseInt(dayjs(program.start).format('mm'));
    return start - end == 5 && !isBlankProgram(program);
  };

  /**
   * 番組の種類を示すコードを返す
   * @example [**f2ea.img**]
   * @return [] | undefined
   */
  const getScheduleTypeCodes = (text: string) => {
    if (!text) return text;
    const codes: any | undefined = [];
    const sleep = (ms = 150) => new Promise((resolve) => setTimeout(resolve, ms));
    (async () => {
      const sleeps = [];
      for (const code of SCHEDULE_TYPE_CODES) {
        if (~text.indexOf(code)) {
          sleeps.push(
            (async () => {
              codes.push(code);
              await sleep();
            })()
          );
        }
      }
      await Promise.all(sleeps);
    })();
    return codes[0] ? codes : undefined;
  };

  /**
   * 番組の種類を示すコードから対応した1字を返す
   * @example 新 | 終 | 生 | 字 | 二
   * @return string
   */
  const getScheduleTypeCharacter = (code: string) => {
    if (!code) return '';
    for (const scheduleType of SCHEDULE_TYPES) {
      if (scheduleType.code === code) {
        return scheduleType.character;
      }
    }
  };

  /**
   * 番組タイトルから番組の種類を示すコードを除外した文字列を返す
   * @return string
   */
  const formatTitle = (title: string | null) => {
    if (!title || title === null) return '';
    let formattedTitle = title;
    for (const code of SCHEDULE_TYPE_CODES) {
      formattedTitle = formattedTitle.replace(code, '');
    }
    return formattedTitle;
  };

  /**
   * エピタイトルを表示させるかの判定（空文字・nullではないか）
   * @return boolean
   */
  const isEptitle = (title: string | null): boolean => {
    const target = title?.trim();
    if (title === null || target?.length === 0) {
      return false;
    } else {
      return true;
    }
  };

  return {
    DAILY_SCHEDULE_HOUR_START,
    DAILY_SCHEDULE_HOUR_END,
    HOURS_PER_DAY,
    BLANK_PROGRAM_ID,

    date,
    data,
    month,
    selectableDate,
    getSchedule,
    formattedPrograms,
    blockPerHour,
    hideMinute,
    startMinute,
    episodeNumber,
    ampm,
    isActiveHour,
    isActiveProgram,
    isBlankProgram,
    init,
    formatProgram,
    isFiveMinute,
    getScheduleTypeCodes,
    getScheduleTypeCharacter,
    formatTitle,
    isEptitle,
  };
};

type ScheduleType = ReturnType<typeof useSchedule>;
export const ScheduleKey: InjectionKey<ScheduleType> = Symbol('Schedule');
